import { Component, Inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilKeyChanged, filter, first, map, pairwise, switchMap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { ConfirmDialog, DialogService } from '../../dialogs';
import { GearConfigComponent } from '../gear-config/gear-config.component';
import {
  ContextWithOption,
  GearVersionSelection,
  GearVersionSelectorComponent,
  SelectGearContext,
} from '../gear-version-selector/gear-version-selector.component';
import { GearService } from '../gear.service';
import { orderByLatest, supportsAlwaysLatest } from '../gear.utils';
import { ProjectsService } from '../../projects/projects.service';
import { RunGearDialog } from '../run-gear-dialog/run-gear-dialog.component';
import { isLabEdition } from '../../shared/container.utils';
import { GearRule } from '../../shared/models/gear-rule.model';
import { Gear, GearConfigInvocation, GearSeries } from '../../shared/models/gear.model';
import { firstValueFrom, replayValue } from '../../shared/observable.utils';
import { isContainerId } from '../../shared/string.utils';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { CreateQueryResult, injectQuery } from '@tanstack/angular-query-experimental';
import { toObservable } from '@angular/core/rxjs-interop';
import { Project } from 'app/src/shared/models/project.model';
import { Group } from 'app/src/shared/models/group.model';
import { ProjectsQueryService } from 'app/src/projects/projects-query.service';

type ContextOptions = Record<SelectGearContext, boolean>

interface DialogData {
  context?: SelectGearContext
  contextOptions?: ContextOptions
  gears?: Gear[]
  gearFilter?: (gear: Gear) => boolean
  gearRule?: Pick<GearRule, '_id' | 'project_id' | 'gear_id' | 'auto_update' | 'config'>
  replaceInvalidGear?: boolean
}

export interface DialogResult {
  alwaysLatest: boolean
  gearConfig: GearConfigInvocation
  selectedGear: Gear
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'select-gear-dialog',
  },
  selector: 'select-gear-dialog',
  styleUrls: ['./select-gear-dialog.component.scss'],
  templateUrl: './select-gear-dialog.component.html',
})
export class SelectGearDialog implements OnInit {
  @ViewChild(GearVersionSelectorComponent, { static: false }) gearSelector?: GearVersionSelectorComponent
  @ViewChild(GearConfigComponent, { static: false }) gearConfig?: GearConfigComponent

  readonly billingLabels: Record<string, string> = {} // gear.gear.name -> label
  context: ContextWithOption = this.data.context ?? 'option'
  contextOptions = this.data.contextOptions ?? { analysis: true, data: true }
  readonly forms = {
    alwaysLatest: new FormControl(false),
    gearSelection: new FormControl({ name: null, id: null, label: null }),
    config: new FormControl({}),
  }
  gearRestrictionsEnabled = this.configService.isFeatureEnabled('gear_access')
  gears: Record<string, Gear> = {}
  gearSeriesMap: Record<string, GearSeries> = {} // gear.name keys
  isLoading = true
  unfilteredGears: Record<string, Gear> = {}

  readonly projectId: string | null = isContainerId(this.data?.gearRule?.project_id) ? this.data?.gearRule?.project_id : null;
  readonly projectQuery = injectQuery(() => this.projectQueryService.getProject(signal(this.projectId)));


  readonly alwaysLatest$ = replayValue(this.forms.alwaysLatest)
  readonly configDiff$ = replayValue(this.forms.gearSelection).pipe(
    distinctUntilKeyChanged('id'),
    pairwise(),
    map(([prev, next]: GearVersionSelection[]) => {
      const prevGear = this.unfilteredGears[prev.id];
      const nextGear = this.unfilteredGears[next.id];
      if (prevGear?.gear?.name !== nextGear?.gear?.name) {
        return null;
      }
      const prevConfigKeys = Object.keys(prevGear.gear?.config ?? {});
      const nextConfigKeys = Object.keys(nextGear.gear?.config ?? {});
      const diff = {
        from: prevGear.gear?.version,
        to: nextGear.gear?.version,
        removed: difference(prevConfigKeys, nextConfigKeys),
        added: difference(nextConfigKeys, prevConfigKeys),
      };
      return diff.removed.length > 0 || diff.added.length > 0 ? diff : null;
    }),
  )
  readonly isValid$ = combineLatest([
    replayValue(this.forms.gearSelection),
    replayValue(this.forms.config),
  ]).pipe(map(() => Boolean(
    this.selectedGearId && this.gearConfig && !this.gearConfig.hasErrors && Object.values(this.forms).every(form => !form.invalid)
  )))
  readonly supportsAlwaysLatest$ = replayValue(this.forms.gearSelection).pipe(
    filter(({ id }) => id),
    map(({ id }) => {
      const selectedGear = this.gears[id];
      const latestVersion = orderByLatest(Object.values(this.gears).filter(gear => gear.gear.name === selectedGear?.gear?.name))[0];
      return latestVersion ? supportsAlwaysLatest(latestVersion) : false;
    }),
  )

  private messages = {
    dialog: TranslationBundle.EMPTY,
  }
  private subscriptions = {
    messages: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private dialogRef: MatDialogRef<RunGearDialog, DialogResult>,
    private dialogService: DialogService,
    private gearService: GearService,
    private projectsService: ProjectsService,
    private translate: TranslationService,
    private projectQueryService: ProjectsQueryService,
  ) {
    this.subscriptions.messages = this.translate.getBundle('selectGearDialog').subscribe(messages => {
      this.messages.dialog = messages;
    });

    const projectQuery: CreateQueryResult<Project | null> = this.projectQuery;
    let project$: Observable<{ project: Project; group: Group}> = of(null);
    if (this.projectId) {
      project$ = toObservable(projectQuery.data).pipe(
        switchMap(project => this.projectsService.getCachedGroup(project.group)
          .pipe(
            catchError(() => of(null)),
            map(group => ({ project, group })),
          )
        ),
        catchError(() => of(null)),
      );
    }

    combineLatest([
      this.gearService.getAllVersions({ projectId: this.projectId }),
      this.gearService.getAllGearSeries(),
      this.configService.getSiteSettings(),
      this.projectId ? project$ : of(null),
    ]).pipe(first()).subscribe(([allGears, allGearSeries, siteSettings, ruleParents]) => {
      this.isLoading = false;
      const gearSeriesMap: Record<string, GearSeries> = {};
      for (const series of allGearSeries) {
        gearSeriesMap[series.name] = series;
      }
      if (!this.projectId && this.gearRestrictionsEnabled) {
        // filter out gears with ANY restriction when in a site context
        allGears = allGears.filter(gearVersion => gearSeriesMap[gearVersion.gear.name]?.is_restricted !== true);
      }
      // use gears if present, apply gear filter if present
      const filteredVersions = (this.data.gears ?? allGears).filter(this.data.gearFilter ?? (() => true));
      this.gears = keyBy(filteredVersions, '_id');
      this.unfilteredGears = keyBy(allGears, '_id');

      const selectedGearId = (this.data.replaceInvalidGear && filteredVersions.length > 0) ? filteredVersions[0]._id : this.data.gearRule?.gear_id;
      const selectedGear = allGears.find(version => version._id === selectedGearId);
      if (selectedGear) {
        this.context = selectedGear.category === 'analysis' ? 'analysis' : 'data';
        this.contextOptions = this.context === 'analysis' ? { data: false, analysis: true } : { data: true, analysis: false };
        this.forms.gearSelection.reset({ id: selectedGear._id, name: selectedGear.gear.name, label: selectedGear.gear.label });
      }
      const gearConfig = this.data.gearRule?.config;
      if (gearConfig) {
        this.forms.config.reset(gearConfig);
      }
      this.forms.alwaysLatest.reset(Boolean(this.data.gearRule?.auto_update));

      // disable/enable config according to valid always latest value
      // reset always latest value if it's not supported
      combineLatest([
        this.alwaysLatest$,
        this.supportsAlwaysLatest$,
      ]).subscribe(([alwaysLatest, supportsAlwaysLatest]) => {
        if (alwaysLatest && supportsAlwaysLatest) {
          this.forms.config.disable();
        } else {
          if (alwaysLatest && !supportsAlwaysLatest) {
            this.forms.alwaysLatest.setValue(false);
          }
          this.forms.config.enable();
        }
      });

      // when always latest is toggled on (by the user):
      // confirm if pending config
      // lock the version select to latest
      // reset config to defaults
      this.alwaysLatest$.pipe(
        filter(Boolean),
      ).subscribe(() => {
        const confirm$ = !this.gearConfig || isEqual(this.forms.config.value, this.gearConfig.getDefaults())
          ? of(true)
          : this.dialogService.open(ConfirmDialog, {
            data: { content: this.messages.dialog.get('alwaysLatest.warning') },
          }).afterClosed();
        confirm$.subscribe(proceed => {
          if (proceed) {
            this.gearSelector?.selectLatestVersion();
            this.gearConfig?.resetToDefaults();
          } else {
            this.forms.alwaysLatest.setValue(false);
          }
        });
      });

      if (configService.isFeatureEnabled('multiproject')) {
        const centerGears = new Set(siteSettings.center_gears || []);
        let billingLabel: string;
        if (this.projectId) {
          if (ruleParents && isLabEdition(ruleParents.project)) {
            // display which lab/group will be billed
            billingLabel = ruleParents.group?.label || ruleParents.project.group;
          } else {
            // filter down to gears that are center-billed
            this.gears = keyBy(Object.values(this.gears).filter(gear => centerGears.has(gear.gear.name)), '_id');
          }
        } else {
          // generic message for site gear rule templates:
          // "The project lab will be billed for this job"
          billingLabel = 'project';
        }
        if (billingLabel) {
          for (const gear of Object.values(this.gears)) {
            if (!centerGears.has(gear.gear.name)) {
              this.billingLabels[gear.gear.name] = billingLabel;
            }
          }
        }
      }

      if (this.context === 'option' && !this.hasContextOptions) {
        this.context = Object.keys(this.contextOptions)
          .find(context => this.contextOptions[context]) as SelectGearContext;
      }
    });
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass('padding-0');
    this.dialogRef.updatePosition({ top: '30px' });
  }

  get hasContextOptions(): boolean {
    return Object.values(this.contextOptions).filter(Boolean).length > 1;
  }

  get selectedGearId(): string {
    return this.forms.gearSelection.value?.id;
  }

  get isEditMode(): boolean {
    return Boolean(this.data.gearRule?._id);
  }

  async saveChanges(): Promise<void> {
    const [isValid, supportsAlwaysLatest] = await firstValueFrom(combineLatest([
      this.isValid$,
      this.supportsAlwaysLatest$,
    ]));
    if (isValid) {
      this.dialogRef.close({
        alwaysLatest: supportsAlwaysLatest && this.forms.alwaysLatest.value,
        gearConfig: this.forms.config.value,
        selectedGear: this.gears[this.selectedGearId],
      });
    }
  }
}
